import React, { useState } from 'react';
import {
  TabSection,
  SectionTitle,
  SectionDescription,
  UserCard,
  UserInfo,
  UserDetails,
  UserName,
  UserEmail,
  ActionButton,
  RoleSelect
} from '../../../pages/admin/components/styled';

const PlatformSettingsTab: React.FC = () => {
  const [settings, setSettings] = useState({
    defaultCredits: 100,
    systemMaintenance: false,
    newUserRegistration: true,
    emailNotifications: true,
    analyticsCollection: true
  });

  const serviceTypes = [
    { id: 'flashcheck', name: 'FlashCheck Analysis', creditsRequired: 5, isActive: true },
    { id: 'document-review', name: 'Document Review', creditsRequired: 10, isActive: true },
    { id: 'data-analysis', name: 'Data Analysis', creditsRequired: 15, isActive: false }
  ];

  const handleSettingChange = (key: string, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    // In real app, this would make an API call to update settings
  };

  return (
    <TabSection>
      <SectionTitle>⚙️ Platform Settings</SectionTitle>
      <SectionDescription>
        Configure global platform settings, pricing, service types, and system behavior.
        Changes here affect all companies and users.
      </SectionDescription>

      {/* System Settings */}
      <div style={{ marginBottom: '32px' }}>
        <h3 style={{ color: '#f8fafc', marginBottom: '16px', fontSize: '1.2rem' }}>System Settings</h3>
        <div style={{ display: 'grid', gap: '16px' }}>
          {[
            {
              key: 'systemMaintenance',
              label: 'System Maintenance Mode',
              description: 'Enable to prevent new logins and show maintenance message',
              type: 'boolean'
            },
            {
              key: 'newUserRegistration',
              label: 'Allow New User Registration',
              description: 'Allow new users to register via SSO',
              type: 'boolean'
            },
            {
              key: 'emailNotifications',
              label: 'Email Notifications',
              description: 'Send system-wide email notifications',
              type: 'boolean'
            },
            {
              key: 'analyticsCollection',
              label: 'Analytics Collection',
              description: 'Collect usage analytics and performance data',
              type: 'boolean'
            }
          ].map((setting) => (
            <UserCard key={setting.key} $isActive={true}>
              <UserInfo>
                <UserDetails>
                  <UserName>{setting.label}</UserName>
                  <UserEmail>{setting.description}</UserEmail>
                </UserDetails>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={settings[setting.key as keyof typeof settings] as boolean}
                      onChange={(e) => handleSettingChange(setting.key, e.target.checked)}
                      style={{ transform: 'scale(1.2)' }}
                    />
                    <span style={{ color: settings[setting.key as keyof typeof settings] ? '#10b981' : '#94a3b8' }}>
                      {settings[setting.key as keyof typeof settings] ? 'Enabled' : 'Disabled'}
                    </span>
                  </label>
                </div>
              </UserInfo>
            </UserCard>
          ))}
        </div>
      </div>

      {/* Credit Settings */}
      <div style={{ marginBottom: '32px' }}>
        <h3 style={{ color: '#f8fafc', marginBottom: '16px', fontSize: '1.2rem' }}>Credit Settings</h3>
        <div style={{ display: 'grid', gap: '16px' }}>
          {[
            {
              key: 'defaultCredits',
              label: 'Default Credits for New Companies',
              description: 'Number of credits new companies receive',
              type: 'number',
              min: 0,
              max: 1000,
              step: 1
            }
          ].map((setting) => (
            <UserCard key={setting.key} $isActive={true}>
              <UserInfo>
                <UserDetails>
                  <UserName>{setting.label}</UserName>
                  <UserEmail>{setting.description}</UserEmail>
                </UserDetails>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="number"
                    value={settings[setting.key as keyof typeof settings] as number}
                    onChange={(e) => handleSettingChange(setting.key, parseFloat(e.target.value))}
                    min={setting.min}
                    max={setting.max}
                    step={setting.step || 1}
                    style={{
                      background: '#1e293b',
                      border: '1px solid #334155',
                      borderRadius: '6px',
                      padding: '8px 12px',
                      color: '#f8fafc',
                      width: '120px'
                    }}
                  />
                  <ActionButton variant="secondary" onClick={() => {}}>
                    Save
                  </ActionButton>
                </div>
              </UserInfo>
            </UserCard>
          ))}
        </div>
      </div>

      {/* Service Types */}
      <div>
        <h3 style={{ color: '#f8fafc', marginBottom: '16px', fontSize: '1.2rem' }}>Service Types & Pricing</h3>
        <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
          <ActionButton>+ Add Service Type</ActionButton>
          <ActionButton variant="secondary">Import/Export</ActionButton>
        </div>
        <div style={{ display: 'grid', gap: '16px' }}>
          {serviceTypes.map((service) => (
            <UserCard key={service.id} $isActive={service.isActive}>
              <UserInfo>
                <UserDetails>
                  <UserName>{service.name}</UserName>
                  <UserEmail>Credits Required: {service.creditsRequired}</UserEmail>
                </UserDetails>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <RoleSelect
                    value={service.isActive ? 'active' : 'inactive'}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {}}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </RoleSelect>
                  <input
                    type="number"
                    value={service.creditsRequired}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {}}
                    min="1"
                    max="100"
                    style={{
                      background: '#1e293b',
                      border: '1px solid #334155',
                      borderRadius: '6px',
                      padding: '8px 12px',
                      color: '#f8fafc',
                      width: '80px'
                    }}
                  />
                  <ActionButton variant="secondary">Edit</ActionButton>
                  <ActionButton variant="secondary">Delete</ActionButton>
                </div>
              </UserInfo>
            </UserCard>
          ))}
        </div>
      </div>
    </TabSection>
  );
};

export default PlatformSettingsTab;
